/**
 * Versioned, character-independent entry contract for Realtime guides.
 *
 * The browser may choose a source identity and opening behaviour, but it may
 * never provide prompt text or factual content. Contextual facts cross the
 * trusted host boundary through `GuideEntryContextResolver` and are pinned to
 * the session before the Realtime connection is created.
 */

export const GUIDE_ENTRY_CONTEXT_PROTOCOL = 'agentos.guide-entry.v1' as const

export type GuideEntryMode = 'generic' | 'contextual_briefing' | 'resume' | 'silent'
export type GuideEntryLifecycleScope = 'host_view' | 'app_session'
export type GuideEntrySourceKind = 'route' | 'poi' | 'species' | 'story'
export type GuideEntryOpening = 'introduce_then_wait' | 'brief_then_wait' | 'wait_silently'

export interface GuideEntrySource {
  readonly kind: GuideEntrySourceKind
  readonly id: string
}

export interface GuideEntryContext {
  readonly version: 1
  readonly entry_mode: GuideEntryMode
  readonly lifecycle_scope: GuideEntryLifecycleScope
  readonly source: GuideEntrySource
  /** Opaque server-issued lookup revision. Never factual or instructional text. */
  readonly facts_ref?: string
  /** BCP-47 language selected by the host, e.g. `de` or `en-US`. */
  readonly language: string
  readonly opening: GuideEntryOpening
}

/** Trusted result returned by the host's server-backed resolver. */
export interface ResolvedGuideEntryContext {
  readonly protocol: typeof GUIDE_ENTRY_CONTEXT_PROTOCOL
  readonly source: GuideEntrySource
  readonly facts_ref: string
  /** Server-curated facts only. The resolver must reject client-authored text. */
  readonly context_line: string
}

export type GuideEntryContextResolver = (
  entry: GuideEntryContext,
) => Promise<ResolvedGuideEntryContext>

export class GuideEntryContextViolation extends Error {
  readonly code: string

  constructor(code: string) {
    super(`guide_entry_context:${code}`)
    this.name = 'GuideEntryContextViolation'
    this.code = code
  }
}

const ENTRY_MODES = new Set<GuideEntryMode>(['generic', 'contextual_briefing', 'resume', 'silent'])
const LIFECYCLE_SCOPES = new Set<GuideEntryLifecycleScope>(['host_view', 'app_session'])
const SOURCE_KINDS = new Set<GuideEntrySourceKind>(['route', 'poi', 'species', 'story'])
const OPENINGS = new Set<GuideEntryOpening>(['introduce_then_wait', 'brief_then_wait', 'wait_silently'])
const ENTRY_KEYS = new Set(['version', 'entry_mode', 'lifecycle_scope', 'source', 'facts_ref', 'language', 'opening'])
const SOURCE_KEYS = new Set(['kind', 'id'])
const RESOLVED_KEYS = new Set(['protocol', 'source', 'facts_ref', 'context_line'])
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/
const SAFE_FACTS_REF = /^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,255}$/
const BCP_47 = /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/

function record(value: unknown, code: string): Record<string, unknown> {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    throw new GuideEntryContextViolation(code)
  }
  return value as Record<string, unknown>
}

function rejectUnknownKeys(value: Record<string, unknown>, allowed: Set<string>, code: string): void {
  if (Object.keys(value).some((key) => !allowed.has(key))) {
    throw new GuideEntryContextViolation(code)
  }
}

function normalizeSource(value: unknown): GuideEntrySource {
  const source = record(value, 'invalid_source')
  rejectUnknownKeys(source, SOURCE_KEYS, 'unknown_source_field')
  if (!SOURCE_KINDS.has(source.kind as GuideEntrySourceKind)) {
    throw new GuideEntryContextViolation('invalid_source_kind')
  }
  if (typeof source.id !== 'string' || !SAFE_ID.test(source.id)) {
    throw new GuideEntryContextViolation('invalid_source_id')
  }
  return Object.freeze({ kind: source.kind as GuideEntrySourceKind, id: source.id })
}

/** Validate, clone and freeze an untrusted host entry before any side effect. */
export function normalizeGuideEntryContext(value: unknown): GuideEntryContext {
  const entry = record(value, 'invalid_entry')
  rejectUnknownKeys(entry, ENTRY_KEYS, 'unknown_entry_field')

  if (entry.version !== 1) throw new GuideEntryContextViolation('unsupported_version')
  if (!ENTRY_MODES.has(entry.entry_mode as GuideEntryMode)) {
    throw new GuideEntryContextViolation('invalid_entry_mode')
  }
  if (!LIFECYCLE_SCOPES.has(entry.lifecycle_scope as GuideEntryLifecycleScope)) {
    throw new GuideEntryContextViolation('invalid_lifecycle_scope')
  }
  if (!OPENINGS.has(entry.opening as GuideEntryOpening)) {
    throw new GuideEntryContextViolation('invalid_opening')
  }
  if (typeof entry.language !== 'string' || !BCP_47.test(entry.language)) {
    throw new GuideEntryContextViolation('invalid_language')
  }
  if (entry.facts_ref !== undefined && (
    typeof entry.facts_ref !== 'string' || !SAFE_FACTS_REF.test(entry.facts_ref)
  )) {
    throw new GuideEntryContextViolation('invalid_facts_ref')
  }

  const mode = entry.entry_mode as GuideEntryMode
  const opening = entry.opening as GuideEntryOpening
  if (opening === 'brief_then_wait' && mode !== 'contextual_briefing') {
    throw new GuideEntryContextViolation('brief_requires_contextual_mode')
  }
  if (mode === 'contextual_briefing' && opening === 'introduce_then_wait') {
    throw new GuideEntryContextViolation('contextual_mode_requires_brief_or_silent')
  }
  if (mode === 'silent' && opening !== 'wait_silently') {
    throw new GuideEntryContextViolation('silent_mode_requires_silent_opening')
  }

  const normalized: GuideEntryContext = {
    version: 1,
    entry_mode: mode,
    lifecycle_scope: entry.lifecycle_scope as GuideEntryLifecycleScope,
    source: normalizeSource(entry.source),
    language: entry.language,
    opening,
    ...(entry.facts_ref === undefined ? {} : { facts_ref: entry.facts_ref as string }),
  }
  return Object.freeze(normalized)
}

/** Validate that trusted facts belong to the exact source pinned at open(). */
export function normalizeResolvedGuideEntryContext(
  value: unknown,
  entry: GuideEntryContext,
): ResolvedGuideEntryContext {
  const resolved = record(value, 'invalid_resolution')
  rejectUnknownKeys(resolved, RESOLVED_KEYS, 'unknown_resolution_field')
  if (resolved.protocol !== GUIDE_ENTRY_CONTEXT_PROTOCOL) {
    throw new GuideEntryContextViolation('invalid_resolution_protocol')
  }
  const source = normalizeSource(resolved.source)
  if (source.kind !== entry.source.kind || source.id !== entry.source.id) {
    throw new GuideEntryContextViolation('resolution_source_mismatch')
  }
  if (typeof resolved.facts_ref !== 'string' || !SAFE_FACTS_REF.test(resolved.facts_ref)) {
    throw new GuideEntryContextViolation('invalid_resolution_facts_ref')
  }
  if (entry.facts_ref != null && resolved.facts_ref !== entry.facts_ref) {
    throw new GuideEntryContextViolation('resolution_facts_ref_mismatch')
  }
  if (
    typeof resolved.context_line !== 'string'
    || resolved.context_line.trim().length === 0
    || resolved.context_line.length > 6_000
  ) {
    throw new GuideEntryContextViolation('invalid_resolution_context')
  }
  return Object.freeze({
    protocol: GUIDE_ENTRY_CONTEXT_PROTOCOL,
    source,
    facts_ref: resolved.facts_ref,
    context_line: resolved.context_line.trim(),
  })
}

const RESUME_INSTRUCTION =
  `Der Besucher ist zurück und wir setzen ein früheres Gespräch fort. ` +
  `Grüße kurz und knüpfe an das an, was zuletzt besprochen wurde — 1 Satz, ` +
  `dann warte auf die nächste Frage. Kein neues "Grüß Gott, ich bin Dr. Tschauko"-Vorstellungsritual.`

export interface GuideOpeningInstructionOptions {
  readonly hasHistory: boolean
  readonly defaultGreeting: string
  /** Anzeigename der eingestellten Sprache („English", „italiano" …).
   *  PFLICHT-Kontext für jede Öffnung: `response.create`-Instructions
   *  ERSETZEN die Session-Instructions samt Sprach-Direktive für genau
   *  diese Antwort (derselbe Mechanismus wie der narrateTopic-Bug vom
   *  29.07.) — ohne expliziten Sprachnamen kam die Eröffnung deutsch,
   *  obwohl die Seite auf Englisch stand (Alex 2026-08-10). */
  readonly languageName?: string
}

/** Sprachklausel für Öffnungs-Anweisungen. Bei Deutsch leer — die Gehirne
 *  sind deutsch, eine Anweisung „sprich Deutsch" wäre totes Gewicht. */
function sprachKlausel(languageName?: string): string {
  if (!languageName || languageName === 'Deutsch') return ''
  return ` Sprich AUSSCHLIESSLICH ${languageName} — der Besucher hat ${languageName} eingestellt.`
}

/** Build exactly one opening turn, or null for an explicitly silent entry. */
export function guideOpeningInstructions(
  entry: GuideEntryContext | null,
  options: GuideOpeningInstructionOptions,
): string | null {
  const klausel = sprachKlausel(options.languageName)
  if (entry?.opening === 'wait_silently' || entry?.entry_mode === 'silent') return null
  if ((entry == null || entry.entry_mode === 'resume') && options.hasHistory) {
    return RESUME_INSTRUCTION + klausel
  }
  if (entry?.opening === 'brief_then_wait') {
    return `Der Besucher öffnet den Realtime-Guide aus dem gerade sichtbaren ` +
      `${entry.source.kind}-Kontext mit der ID ${entry.source.id}. ` +
      `Gib anhand des serverseitig aufgelösten GUIDE-EINSTIEGSKONTEXTS zuerst ` +
      `eine knappe, natürliche Einordnung in 2–3 Sätzen. Beginne ohne allgemeines ` +
      `Vorstellungsritual, erfinde keine fehlenden Fakten und frage anschließend, ` +
      `was der Besucher wissen möchte.` + klausel
  }
  /* „sinngemäß" und „in der eingestellten Sprache": der Satz ist der Inhalt
     der Begrüßung, nicht ihr Wortlaut. Steht die Sprache auf Italienisch, war
     ein wörtlich vorgegebener deutscher Satz ein Widerspruch zur
     Sprachanweisung, die weiter oben im Systemtext alles überstimmt.
     Die Sprachklausel deckt zusätzlich die Figuren ab, deren Gruß nur
     deutsch vorliegt (Eulen): „sinngemäß" + Zielsprache = übersetzter Gruß. */
  return `Begrüße den Besucher kurz und sinngemäß mit: "${options.defaultGreeting}" — in der eingestellten Sprache, in deinen eigenen Worten.` + klausel
}
